/*
Map screen — primary view. Hosts the MapLibre canvas, the FAB stack
(remarquables / arboretum / profile), the cold-start splash, a
transient toast for capture feedback, and the bottom-sheet detail
panel that opens on tap of a tree pin.

Capture model (sheet-driven, mirrors Android):
- Tap a tree pin → bottom sheet opens with the arbre's identity (species
  name if revealed; "Inconnu" otherwise), live distance, per-arbre
  details if captured (height / circ / address / id), and a "📷 Capturer"
  button gated by proximity (≤ kMaxArbreMeters).
- Tapping "Capturer" runs runCaptureFlow against THAT arbre. The
  handler is synchronous through to capturePhoto() so the iOS user-
  gesture context is preserved.
*/

// Type-only import keeps MapView (and its `maplibre-gl` dependency, ~600 KB
// gzipped) out of the static graph. The runtime import lives inside
// `mountMapScreen` so MapLibre is only fetched + parsed when the user
// actually lands on the map. Profile / Arboretum / Species / Badges /
// Welcome cold-load drops by the size of the MapLibre chunk.
import type { MapController, ArbreTapPayload } from '../map/MapView'
import { navigate, kPaths } from '../router'
import { runCaptureFlow, kMaxArbreMeters, kHysteresisMeters, type CaptureStatus, type TargetArbre } from '../capture/captureFlow'
import { triggerCaptureFeedback } from '../capture/feedback'
import { ensureSpeciesIndex, speciesNameFor, type SpeciesName } from '../data/speciesIndex'
import { capturedArbreIds, capturedSpeciesIndices } from '../data/captureRepository'
import { ensureArbresFeatures, applyDiscoveryToFeatures } from '../data/arbresFeatures'
import { ensureArbresDetails, detailsFor, type ArbreDetails } from '../data/arbresDetails'
import { subscribeFix, haversineMeters, getLatestFix, type FreshFix } from '../capture/geolocation'
import { renderSplashHtml, startSplash } from './components/SplashOverlay'
import { openSearchSheet } from './components/SearchSheet'
import { renderSuggestionCardShell, updateSuggestionCard } from './components/SuggestionCard'
import { computeDiscoverSuggestion, type DiscoverSuggestion } from '../data/discoverSuggestion'
import { subscribeRoute, setActiveRoute } from '../data/routeState'
import type { RoutePlan } from '../data/routePlanner'
import { listCaptures } from '../data/captureRepository'
import { tryPersistStorage } from '../data/persistentStorage'

// Pending banner state stashed across the species-celebrate detour: the
// first capture immediately navigates to the species detail screen, so we
// can't render the banner inline — we set a localStorage flag and read it
// the next time the map is mounted.
const kPersistBannerKey: string = 'canopaname.banner.persistStorage'  // localStorage key
type PersistBannerState = 'granted' | 'denied'  // values written under kPersistBannerKey

let fActiveMap: MapController | null = null  // module-level so we can clean up on screen swap
let fCaptureInFlight: boolean = false  // simple lock — disallow concurrent captures
let fSheetTarget: ArbreTapPayload | null = null  // currently-open arbre, full payload incl. lat/lon/hm/cm/addr/arr
let fCapturedArbres: Set<number> = new Set()  // arbreIds captured (gates "Fiche remarquable" + per-arbre details)
let fCapturedSpecies: Set<number> = new Set()  // speciesIndex captured (gates "Fiche espèce" + post-capture reveal of the species name)
let fSheetFixUnsub: (() => void) | null = null  // active geolocation subscription, cancelled on sheet close
let fMountGen: number = 0  // bumped on every mount; lets the async MapLibre import bail out if the user already navigated away
let fSuggestionFixUnsub: (() => void) | null = null  // global geolocation subscription that drives the discover-next card
let fLastSuggestion: DiscoverSuggestion | null = null  // memoize the last result so an identical fix doesn't re-paint
let fSuggestionDismissed: boolean = false  // session-only — × hides until the user makes a capture (which clears it)
let fRouteUnsub: (() => void) | null = null  // route-state subscription, torn down on remount

/*
Mount the map screen. Builds the DOM, lazily fetches the MapLibre
chunk, mounts the map, then wires the FAB stack + arbre-tap → sheet →
capture pipeline.

Returns a Promise so the dynamic import of `../map/MapView` can be
awaited, but the router treats it as void — the splash overlay covers
the chunk-load gap on cold start, and `fMountGen` aborts the async
work if the user navigated to a different screen meanwhile.
*/
export async function mountMapScreen(inRoot: HTMLElement): Promise<void>
{
	const vGen = ++fMountGen  // capture this mount's generation; compare after every await

	if (fActiveMap)
	{
		fActiveMap.destroy()
		fActiveMap = null
	}
	if (fSheetFixUnsub) { fSheetFixUnsub(); fSheetFixUnsub = null }
	if (fSuggestionFixUnsub) { fSuggestionFixUnsub(); fSuggestionFixUnsub = null }
	if (fRouteUnsub) { fRouteUnsub(); fRouteUnsub = null }
	fLastSuggestion = null
	fSuggestionDismissed = false  // each fresh mount gets one shot at the card

	inRoot.innerHTML = `
		<div class="screen">
			<div id="map-canvas" class="map-canvas"></div>
			<div class="fab-stack">
				<button class="fab" id="fab-search" title="Rechercher">🔍</button>
				<button class="fab" id="fab-remarquables" title="Remarquables">⭐</button>
				<button class="fab" id="fab-arboretum" title="Arboretum">🌳</button>
				<button class="fab" id="fab-profile" title="Profil">👤</button>
			</div>
			<div class="toast" id="toast" data-open="false" role="status" aria-live="polite"></div>
			<span class="map-beta-pill" role="note" title="Version bêta">BÊTA</span>
			${renderSuggestionCardShell()}
			${renderRouteBanner()}
			${renderPersistBanner()}
			${renderArbreSheet()}
			${renderSplashHtml()}
		</div>
	`
	void startSplash(inRoot)

	maybeShowPersistBanner(inRoot)

	inRoot.querySelector('#fab-arboretum')?.addEventListener('click', () => navigate(kPaths.arboretum()))
	inRoot.querySelector('#fab-profile')?.addEventListener('click', () => navigate(kPaths.profile()))
	inRoot.querySelector('#fab-remarquables')?.addEventListener('click', () => navigate(kPaths.remarquables()))
	inRoot.querySelector('#fab-search')?.addEventListener('click', () => onSearchClick(inRoot))
	inRoot.querySelector('#suggestion-close')?.addEventListener('click', () => {
		fSuggestionDismissed = true
		updateSuggestionCard(inRoot, null)
	})
	inRoot.querySelector('#discover-suggestion-card')?.addEventListener('click', (inEv) => {
		// The × close button has its own handler with stopPropagation
		// implicit through the button hierarchy — but defensive: ignore
		// taps that originated on the close.
		if ((inEv.target as HTMLElement)?.id === 'suggestion-close') return
		if (!fLastSuggestion) return
		const vSugg = fLastSuggestion
		fActiveMap?.flyTo({ lat: vSugg.feature.lat, lon: vSugg.feature.lon }, 17)
		openSheetFor(inRoot, {
			id: vSugg.feature.id,
			speciesIndex: vSugg.feature.speciesIndex,
			remarquable: vSugg.feature.remarquable,
			lat: vSugg.feature.lat,
			lon: vSugg.feature.lon,
		})
	})

	// Recompute the suggestion whenever the GPS fix moves. The top
	// cleanup already disposed of any prior subscription on remount,
	// so we just attach a fresh one for this mount; the next call to
	// mountMapScreen will tear it back down.
	fSuggestionFixUnsub = subscribeFix((inFix) => {
		if (vGen !== fMountGen) return
		void refreshSuggestion(inRoot, inFix)
	})

	// Walking-route subscription. SpeciesScreen pushes a plan into
	// routeState before navigating here; the subscriber fires once
	// immediately with the current value (likely the freshly-set
	// plan), then on every change. Tearing down on remount is handled
	// at the top of this function alongside the suggestion unsub.
	fRouteUnsub = subscribeRoute((inPlan) => {
		if (vGen !== fMountGen) return
		void applyActiveRoute(inRoot, inPlan)
	})

	inRoot.querySelector('#route-banner-exit')?.addEventListener('click', () => {
		setActiveRoute(null)
	})
	inRoot.querySelector('#sheet-backdrop')?.addEventListener('click', () => closeSheet(inRoot))
	inRoot.querySelector('#sheet-close')?.addEventListener('click', () => closeSheet(inRoot))
	inRoot.querySelector('#sheet-species')?.addEventListener('click', () => onSheetSpecies())
	inRoot.querySelector('#sheet-remarquable')?.addEventListener('click', () => onSheetRemarquable())
	inRoot.querySelector('#sheet-capture')?.addEventListener('click', () => onSheetCapture(inRoot))
	inRoot.querySelector('#persist-banner-close')?.addEventListener('click', () => dismissPersistBanner(inRoot))

	void ensureSpeciesIndex().catch((inErr: unknown) => {
		console.warn('Species index preload failed', inErr)
	})

	// Lazy-load MapLibre. The chunk is cached after the first load, so
	// returning to the map later is essentially free.
	let vMountMap: typeof import('../map/MapView').mountMap
	try
	{
		vMountMap = (await import('../map/MapView')).mountMap
	}
	catch (vErr)
	{
		console.error('MapLibre chunk failed to load', vErr)
		return
	}
	if (vGen !== fMountGen) return  // user navigated away during the chunk load — bail out before touching the DOM

	const vCanvas = inRoot.querySelector('#map-canvas') as HTMLElement | null  // MapLibre mount target — null if the DOM was wiped
	if (!vCanvas) return
	fActiveMap = vMountMap(vCanvas)
	fActiveMap.onPointTap((inPayload) => openSheetFor(inRoot, inPayload))

	void Promise.all([capturedArbreIds(), capturedSpeciesIndices()]).then((inSets) => {
		if (vGen !== fMountGen) return  // late resolve after a navigation away
		fCapturedArbres = inSets[0]
		fCapturedSpecies = inSets[1]
		fActiveMap?.applyInitialDiscovered(fCapturedArbres)
	}).catch((inErr: unknown) => { console.warn('Initial discovery state failed', inErr) })

	// Pin colours are handled by applyInitialDiscovered above (cheap
	// per-feature `setFeatureState` calls). We DO NOT auto-enrich on cold
	// start: enrichAndPushDiscovery rebuilds the entire 213k-feature
	// FeatureCollection and hands it to MapLibre's setData, which rebuilds
	// the cluster index. On iOS Safari Standalone (WebContent ~200-400 MB
	// cap), running that on every cold start with stored captures was
	// killing the page mid-load — and the same trigger fired on every
	// reopen, leaving the user stuck on a white screen. Trade-off: the
	// cluster bubbles stay grey at cold start until the user makes a fresh
	// capture in the running session. Pin colours (the per-tree state)
	// are correct from the first frame.
}

/*
Bottom-sheet markup. Rendered once at mount; visibility driven by data-open.
Inner content (#sheet-title / #sheet-details / #sheet-capture text…) is
rewritten on each tap.
*/
function renderArbreSheet(): string
{
	return `
		<div class="sheet" id="arbre-sheet" data-open="false" role="dialog" aria-labelledby="sheet-title">
			<div class="sheet-backdrop" id="sheet-backdrop"></div>
			<div class="sheet-panel">
				<button class="sheet-close" id="sheet-close" aria-label="Fermer">×</button>
				<div class="sheet-glyph" id="sheet-glyph" aria-hidden="true">🌳</div>
				<h2 class="display sheet-title" id="sheet-title">Arbre</h2>
				<div class="sheet-subtitle" id="sheet-subtitle"></div>
				<div class="sheet-details" id="sheet-details" data-visible="false"></div>
				<div class="sheet-actions">
					<button class="sheet-button sheet-button-primary" id="sheet-capture">📷 Capturer</button>
					<button class="sheet-button" id="sheet-species" data-visible="true">Fiche espèce</button>
					<button class="sheet-button" id="sheet-remarquable" data-remarquable="false">Fiche remarquable</button>
				</div>
			</div>
		</div>
	`
}

/*
Open the bottom sheet for a freshly-tapped arbre.
- Discovery rule on the title (species name only revealed after first capture of that species).
- Per-arbre details revealed only once captured (Android Phase 12 contract).
- Live distance subscription drives the capture button state.
*/
function openSheetFor(inRoot: HTMLElement, inPayload: ArbreTapPayload): void
{
	fSheetTarget = inPayload
	// Spotlight the tapped arbre with the pulsing ring layer for as
	// long as the sheet is open. Cleared in closeSheet.
	fActiveMap?.setActiveArbre({ lat: inPayload.lat, lon: inPayload.lon })

	const vSpeciesKnown = fCapturedSpecies.has(inPayload.speciesIndex)
	const vArbreCaptured = fCapturedArbres.has(inPayload.id)
	const vName = speciesNameFor(inPayload.speciesIndex)  // {genre, espece, commonName} or null

	const vTitle = vSpeciesKnown
		? (vName?.commonName ?? formatLatin(vName))
		: 'Arbre inconnu'
	const vSubtitle = vSpeciesKnown
		? (vName?.commonName ? formatLatin(vName) : '')
		: 'Capture cet arbre pour révéler son espèce.'
	const vGlyph = inPayload.remarquable ? '⭐' : '🌳'

	setText(inRoot, '#sheet-title', vTitle)
	setText(inRoot, '#sheet-subtitle', vSubtitle)
	setText(inRoot, '#sheet-glyph', vGlyph)

	// Per-arbre details — fetched lazily from the companion details file
	// only when this exact arbre has been captured. The fetch is the
	// first hit's only cost; subsequent sheet opens read from the
	// in-memory map synchronously.
	const vDetails = inRoot.querySelector<HTMLElement>('#sheet-details')
	if (vDetails)
	{
		if (vArbreCaptured)
		{
			vDetails.dataset.visible = 'true'
			const vCachedDetails = detailsFor(inPayload.id)  // sync read; null until first ensureArbresDetails resolves
			if (vCachedDetails)
			{
				vDetails.innerHTML = renderDetailsHtml(inPayload.id, vCachedDetails)
			}
			else
			{
				vDetails.innerHTML = '<div class="sheet-detail-row"><span>Chargement…</span></div>'
				void ensureArbresDetails().then((inMap) => {
					if (fSheetTarget?.id !== inPayload.id) return  // user moved on; bail
					const vEl = inRoot.querySelector<HTMLElement>('#sheet-details')
					if (!vEl) return
					const vD = inMap.get(inPayload.id) ?? null
					vEl.innerHTML = vD ? renderDetailsHtml(inPayload.id, vD) : `<div class="sheet-detail-row sheet-detail-id"><span class="sheet-detail-icon">🏷️</span><span>id OpenData : ${inPayload.id}</span></div>`
				}).catch((inErr: unknown) => { console.warn('Details fetch failed', inErr) })
			}
		}
		else
		{
			vDetails.innerHTML = ''
			vDetails.dataset.visible = 'false'
		}
	}

	// Buttons that depend purely on capture state (no live updates needed).
	const vSpeciesBtn = inRoot.querySelector<HTMLElement>('#sheet-species')
	if (vSpeciesBtn) vSpeciesBtn.setAttribute('data-visible', vSpeciesKnown ? 'true' : 'false')
	const vRemarquableBtn = inRoot.querySelector<HTMLElement>('#sheet-remarquable')
	if (vRemarquableBtn) vRemarquableBtn.setAttribute('data-remarquable', (inPayload.remarquable && vArbreCaptured) ? 'true' : 'false')

	// Cancel any prior subscription before starting a new one — otherwise
	// a rapid tap on a different tree would leave the old listener wired.
	if (fSheetFixUnsub) { fSheetFixUnsub(); fSheetFixUnsub = null }
	fSheetFixUnsub = subscribeFix((inFix) => updateCaptureButtonState(inRoot, inFix))

	inRoot.querySelector<HTMLElement>('#arbre-sheet')?.setAttribute('data-open', 'true')
}

/*
Render the captured-arbre details block. Each row is icon + value; rows
with missing data are skipped (the OpenData CSV has lots of nulls in
hauteur/circonférence for very young or recently-planted trees).
*/
function renderDetailsHtml(inArbreId: number, inDetails: ArbreDetails): string
{
	const vRows: string[] = []  // accumulated row markup, joined at end
	if (typeof inDetails.heightMeters === 'number' && inDetails.heightMeters > 0)
	{
		vRows.push(`<div class="sheet-detail-row"><span class="sheet-detail-icon">📏</span><span>${inDetails.heightMeters} m de haut</span></div>`)
	}
	if (typeof inDetails.circumferenceCm === 'number' && inDetails.circumferenceCm > 0)
	{
		vRows.push(`<div class="sheet-detail-row"><span class="sheet-detail-icon">⭕</span><span>${inDetails.circumferenceCm} cm de circonférence</span></div>`)
	}
	if (inDetails.address)
	{
		const vArrSuffix = inDetails.arrondissement ? ` (${escapeHtml(inDetails.arrondissement)})` : ''
		vRows.push(`<div class="sheet-detail-row"><span class="sheet-detail-icon">📍</span><span>${escapeHtml(inDetails.address)}${vArrSuffix}</span></div>`)
	}
	else if (inDetails.arrondissement)
	{
		vRows.push(`<div class="sheet-detail-row"><span class="sheet-detail-icon">📍</span><span>${escapeHtml(inDetails.arrondissement)}</span></div>`)
	}
	vRows.push(`<div class="sheet-detail-row sheet-detail-id"><span class="sheet-detail-icon">🏷️</span><span>id OpenData : ${inArbreId}</span></div>`)
	return vRows.join('')
}

/*
Update the capture button text/state based on the user's distance to
the open sheet's target arbre. Called by the fix subscription on every
geolocate event AND once immediately on subscribe (bootstrap).

Hysteresis to absorb GPS jitter:
- Below kMaxArbreMeters (30 m) → enter "ready" (latching).
- Above kMaxArbreMeters + kHysteresisMeters (35 m) → "far".
- In between (30–35 m) → keep the previous state ("ready" stays
  ready, anything else stays disabled). Without this, a 28→32→29 m
  jitter cycle would flicker the button mid-tap.

The captureFlow gate uses `kCaptureCutoffMeters` (35 m) so the button's
"ready" state is never rejected by a stricter check underneath.
*/
function updateCaptureButtonState(inRoot: HTMLElement, inFix: FreshFix | null): void
{
	const vBtn = inRoot.querySelector<HTMLButtonElement>('#sheet-capture')
	if (!vBtn || !fSheetTarget) return
	if (fCaptureInFlight) return  // don't fight the in-flight UI

	if (!inFix)
	{
		vBtn.disabled = true
		vBtn.textContent = '📍 GPS…'
		vBtn.dataset.state = 'pending'
		return
	}

	const vDist = haversineMeters(inFix.lat, inFix.lon, fSheetTarget.lat, fSheetTarget.lon)
	const vWasReady = vBtn.dataset.state === 'ready'  // latching window: ready persists across the 5 m hysteresis band
	const vReady = vDist <= kMaxArbreMeters || (vWasReady && vDist <= kMaxArbreMeters + kHysteresisMeters)

	if (vReady)
	{
		vBtn.disabled = false
		vBtn.textContent = '📷 Capturer'
		vBtn.dataset.state = 'ready'
	}
	else
	{
		vBtn.disabled = true
		vBtn.textContent = `Trop loin (${vDist.toFixed(0)} m)`
		vBtn.dataset.state = 'far'
	}
}

function closeSheet(inRoot: HTMLElement): void
{
	inRoot.querySelector<HTMLElement>('#arbre-sheet')?.setAttribute('data-open', 'false')
	fSheetTarget = null
	fActiveMap?.setActiveArbre(null)  // stop the pulse + clear the ring source
	if (fSheetFixUnsub) { fSheetFixUnsub(); fSheetFixUnsub = null }
}

/*
Open the universal search sheet and wire its three outcomes:
- Species hit → navigate to the species detail screen.
- Arrondissement hit → fly the map to the centroid (zoom 14).
- Arbre hit → fly + open the bottom sheet for that tree, exactly
  as if the user had tapped its pin on the map.
*/
function onSearchClick(inRoot: HTMLElement): void
{
	openSearchSheet({
		onSpecies: (inSk) => navigate(kPaths.species(inSk)),
		onArrondissement: (inInfo) => fActiveMap?.flyTo({ lat: inInfo.lat, lon: inInfo.lon }, 14),
		onArbre: (inFeat) => {
			fActiveMap?.flyTo({ lat: inFeat.lat, lon: inFeat.lon }, 17)
			openSheetFor(inRoot, {
				id: inFeat.id,
				speciesIndex: inFeat.speciesIndex,
				remarquable: inFeat.remarquable,
				lat: inFeat.lat,
				lon: inFeat.lon,
			})
		},
	})
}

function onSheetSpecies(): void
{
	if (!fSheetTarget) return
	navigate(kPaths.species(fSheetTarget.speciesIndex))
}

function onSheetRemarquable(): void
{
	if (!fSheetTarget || !fSheetTarget.remarquable) return
	navigate(kPaths.remarquable(fSheetTarget.id))
}

/*
Sheet "Capturer" handler — keep this synchronous through to the
captureFlow's first call so iOS preserves the user gesture for the
file-input click inside capturePhoto().
*/
function onSheetCapture(inRoot: HTMLElement): void
{
	if (!fSheetTarget) return
	if (fCaptureInFlight) return
	fCaptureInFlight = true

	const vBtn = inRoot.querySelector<HTMLButtonElement>('#sheet-capture')
	if (vBtn) { vBtn.disabled = true; vBtn.textContent = '📷 …' }

	const vTarget: TargetArbre = {
		arbreId: fSheetTarget.id,
		speciesIndex: fSheetTarget.speciesIndex,
		remarquable: fSheetTarget.remarquable,
		lat: fSheetTarget.lat,
		lon: fSheetTarget.lon,
	}
	const vPromise = runCaptureFlow(vTarget, (inStatus) => {
		try { onCaptureStatus(inRoot, inStatus) }
		catch (vErr) { console.warn('Capture status render failed', vErr) }
	})
	vPromise.finally(() => {
		fCaptureInFlight = false
		// Restore the button by re-running the distance check (it'll set
		// the right state — Capturer / Trop loin — based on current GPS).
		updateCaptureButtonState(inRoot, null)
		// Re-subscribe to refresh from latest fix (subscribeFix emits
		// immediately on attach, but we already have an active sub).
	})
}

function onCaptureStatus(inRoot: HTMLElement, inStatus: CaptureStatus): void
{
	if (inStatus.phase === 'cancelled')
	{
		hideToast(inRoot)
		return
	}
	if (inStatus.phase === 'success')
	{
		// Tactile + visual confirmation fires immediately, BEFORE we
		// close the sheet, so the user sees the shutter wash on top of
		// the still-open arbre context — same beat the native iOS
		// camera ships. Vibration silently no-ops on iOS Safari.
		triggerCaptureFeedback()
		const vLabel = inStatus.speciesLabel ?? 'Arbre'
		if (inStatus.target)
		{
			fActiveMap?.setDiscovered(inStatus.target.arbreId)
			fCapturedArbres.add(inStatus.target.arbreId)
			fCapturedSpecies.add(inStatus.target.speciesIndex)
		}
		void enrichAndPushDiscovery()
		if (inStatus.firstCaptureEver) void onFirstCaptureEver()
		// A fresh capture invalidates the previous suggestion (the
		// captured arbre might have been the one we were nudging the
		// user toward). Clear the dismissed flag and recompute against
		// the current fix so the card hops to the next target.
		fSuggestionDismissed = false
		void refreshSuggestion(inRoot, getLatestFix())
		closeSheet(inRoot)
		if (inStatus.firstForSpecies && inStatus.target)
		{
			showToast(inRoot, `✨ Nouvelle espèce : ${vLabel}`, 'success', 1800)
			window.setTimeout(() => navigate(kPaths.species(inStatus.target!.speciesIndex, true)), 600)
		}
		else
		{
			showToast(inRoot, `✓ ${vLabel} capturé !`, 'success', 2400)
		}
		return
	}
	if (inStatus.phase === 'error')
	{
		showToast(inRoot, inStatus.message ?? 'Erreur', 'error', 4200)
		return
	}
	if (inStatus.message) showToast(inRoot, inStatus.message, 'info', null)
}

/*
Markup for the active-route banner. Always rendered (data-open=false
default); applyActiveRoute toggles visibility + writes the contents
when a plan becomes active.
*/
function renderRouteBanner(): string
{
	return `
		<aside class="route-banner" id="route-banner" data-open="false" role="status">
			<span class="route-banner-icon" aria-hidden="true">🚶</span>
			<div class="route-banner-body">
				<span class="route-banner-title" id="route-banner-title">Itinéraire actif</span>
				<span class="route-banner-meta" id="route-banner-meta"></span>
			</div>
			<button class="route-banner-exit" id="route-banner-exit" type="button">Quitter</button>
		</aside>
	`
}

/*
Apply a freshly-set route to the map: push the polyline + waypoints
to MapLibre's source pair, frame the camera around the route's
bounding box, and write the banner contents. Passing null clears
both the layers and the banner.
*/
async function applyActiveRoute(inRoot: HTMLElement, inPlan: RoutePlan | null): Promise<void>
{
	const vBanner = inRoot.querySelector<HTMLElement>('#route-banner')
	const vTitle = inRoot.querySelector<HTMLElement>('#route-banner-title')
	const vMeta = inRoot.querySelector<HTMLElement>('#route-banner-meta')
	if (!vBanner || !vTitle || !vMeta) return

	if (!inPlan)
	{
		fActiveMap?.setActiveRoute(null)
		vBanner.setAttribute('data-open', 'false')
		return
	}

	// Resolve the species label so the banner reads "Platanus
	// hispanica · 8 étapes · 1.6 km". The species index might still
	// be loading on cold-start — best-effort with a fallback.
	let vLabel: string = `Espèce ${inPlan.speciesIndex}`
	try
	{
		await ensureSpeciesIndex()
		const vName = speciesNameFor(inPlan.speciesIndex)
		if (vName) vLabel = vName.commonName ?? `${vName.genre} ${vName.espece}`.trim()
	}
	catch { /* soft */ }

	vTitle.textContent = `Itinéraire · ${vLabel}`
	const vKm = (inPlan.totalMeters / 1000).toFixed(2)
	vMeta.textContent = `${inPlan.waypoints.length} étape${inPlan.waypoints.length > 1 ? 's' : ''} · ${vKm} km`
	vBanner.setAttribute('data-open', 'true')

	fActiveMap?.setActiveRoute({ polyline: inPlan.polyline, waypoints: inPlan.waypoints })

	// Frame the camera around the route. Bbox = min/max of the
	// polyline points (which already includes the start). Pad by
	// 60 px so the stop circles don't kiss the screen edges.
	const vLons = inPlan.polyline.map((inP) => inP[0])
	const vLats = inPlan.polyline.map((inP) => inP[1])
	const vBbox: [number, number, number, number] = [
		Math.min(...vLons),
		Math.min(...vLats),
		Math.max(...vLons),
		Math.max(...vLats),
	]
	fActiveMap?.fitBounds(vBbox, 80)
}

/*
Recompute the discover-next suggestion against the current fix +
captures + features. Cheap (one linear pass over the lightweight
features array) so we run it on every fix update; the GPS already
emits at most a handful of times per minute. No-op when the user
has dismissed the card for this mount, when no fix is available
yet, or when the features index hasn't loaded.
*/
async function refreshSuggestion(inRoot: HTMLElement, inFix: { lat: number; lon: number } | null): Promise<void>
{
	if (fSuggestionDismissed)
	{
		updateSuggestionCard(inRoot, null)
		return
	}
	if (!inFix)
	{
		updateSuggestionCard(inRoot, null)
		return
	}
	try
	{
		const vFeatures = await ensureArbresFeatures()  // cache hit after the first call
		const vCaptures = await listCaptures()
		const vSugg = computeDiscoverSuggestion(inFix, vFeatures, vCaptures)
		fLastSuggestion = vSugg
		updateSuggestionCard(inRoot, vSugg)
	}
	catch (vErr)
	{
		console.warn('Discover suggestion compute failed', vErr)
	}
}

async function enrichAndPushDiscovery(): Promise<void>
{
	if (!fActiveMap) return
	try
	{
		await ensureArbresFeatures()
		const vIds = await capturedArbreIds()
		const vFc = applyDiscoveryToFeatures(vIds)
		if (vFc) fActiveMap.setSourceData(vFc)
	}
	catch (vErr)
	{
		console.warn('Discovery enrichment failed', vErr)
	}
}

/*
First-capture side effect: ask the browser to mark our origin as
persistent storage so iOS doesn't evict the photos + cached geojson
when the device is low on space. The result is stashed in
localStorage under kPersistBannerKey — the next mount of the map
screen reads it and renders an explanatory banner.

We don't render the banner inline because the first-capture flow
immediately navigates to the species celebrate screen, taking the
user away from the map DOM. Stashing the state survives that detour.
*/
async function onFirstCaptureEver(): Promise<void>
{
	try
	{
		const vGranted = await tryPersistStorage()
		const vState: PersistBannerState = vGranted ? 'granted' : 'denied'
		window.localStorage.setItem(kPersistBannerKey, vState)
	}
	catch (vErr)
	{
		console.warn('Persistent-storage request failed', vErr)
	}
}

/*
Banner DOM. Always rendered; visibility driven by data-open. The text
slot is filled from JS based on the localStorage flag — we keep both
copies in code so the banner can adapt to "granted" vs "denied"
without juggling state across reloads.
*/
function renderPersistBanner(): string
{
	return `
		<div class="persist-banner" id="persist-banner" data-open="false" role="status">
			<span class="persist-banner-icon" aria-hidden="true">🛡️</span>
			<span class="persist-banner-text" id="persist-banner-text"></span>
			<button class="persist-banner-close" id="persist-banner-close" aria-label="Fermer">×</button>
		</div>
	`
}

/*
If a pending banner state is present in localStorage, paint the
correct copy and reveal the banner. No-op when the flag is absent
(common case — banner only ever shows once, just after the first
capture, until the user dismisses it).
*/
function maybeShowPersistBanner(inRoot: HTMLElement): void
{
	const vRaw = window.localStorage.getItem(kPersistBannerKey)  // 'granted' | 'denied' | null
	if (vRaw !== 'granted' && vRaw !== 'denied') return
	const vBanner = inRoot.querySelector<HTMLElement>('#persist-banner')
	const vText = inRoot.querySelector<HTMLElement>('#persist-banner-text')
	if (!vBanner || !vText) return
	vText.textContent = vRaw === 'granted'
		? 'Tes captures et tes photos sont protégées : iOS ne pourra pas les effacer pour libérer de l\'espace.'
		: 'Pour qu\'iOS ne supprime pas tes captures en cas de stockage faible, ajoute CanoPaname à l\'écran d\'accueil (Partager → Sur l\'écran d\'accueil).'
	vBanner.dataset.variant = vRaw
	vBanner.setAttribute('data-open', 'true')
}

function dismissPersistBanner(inRoot: HTMLElement): void
{
	inRoot.querySelector<HTMLElement>('#persist-banner')?.setAttribute('data-open', 'false')
	window.localStorage.removeItem(kPersistBannerKey)
}

let fToastTimeout: number | null = null  // pending dismiss timer, null when no timer is queued

function showToast(
	inRoot: HTMLElement,
	inMessage: string,
	inVariant: 'info' | 'success' | 'error',
	inDurationMs: number | null,
): void
{
	const vToast = inRoot.querySelector<HTMLElement>('#toast')
	if (!vToast) return
	vToast.textContent = inMessage
	vToast.dataset.variant = inVariant
	vToast.setAttribute('data-open', 'true')
	if (fToastTimeout !== null) { window.clearTimeout(fToastTimeout); fToastTimeout = null }
	if (inDurationMs !== null)
	{
		fToastTimeout = window.setTimeout(() => {
			vToast.setAttribute('data-open', 'false')
			fToastTimeout = null
		}, inDurationMs)
	}
}

function hideToast(inRoot: HTMLElement): void
{
	inRoot.querySelector<HTMLElement>('#toast')?.setAttribute('data-open', 'false')
	if (fToastTimeout !== null) { window.clearTimeout(fToastTimeout); fToastTimeout = null }
}

function setText(inRoot: HTMLElement, inSelector: string, inText: string): void
{
	const vEl = inRoot.querySelector<HTMLElement>(inSelector)
	if (vEl) vEl.textContent = inText
}

function formatLatin(inName: SpeciesName | null): string
{
	if (!inName) return 'Espèce inconnue'
	const vEspece = inName.espece.length > 0 ? ` ${inName.espece}` : ''
	return `${inName.genre}${vEspece}`
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}
